import { useState } from "react";
import { Box, Typography, IconButton, Tooltip, ClickAwayListener } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

/**
 * @param {Object} props
 * @param {string} props.status - The status text to display (e.g., "PASS", "FAIL", "INCOMPLETE")
 * @param {string} props.issues - The issue description text
 * @param {string} props.issueType - The type of issue (e.g., "ERROR", "WARNING", "INFO")
 * @param {React.ReactNode} props.children - Optional children to render (e.g., StatusCard)
 * @param {boolean} props.showStatusText - Whether to show status text inline (default: false)
 */

const IssueIndicator = ({ status, issues, issueType, children, showStatusText = false, divergentDetails = null, onViewDivergence = null }) => {
  const hasIssue = Boolean(issues?.trim());
  const [issueOpen, setIssueOpen] = useState(hasIssue);

  const getIssueStyle = () => {
    const statusUpper = status?.toUpperCase();

    switch (statusUpper) {
      case "FAIL":
      case "FAILED":
      case "ABORTED":
      case "ABEND":
        return { bgColor: "#d32f2f", icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} /> };

      case "INCOMPLETE":
      case "STOPP":
      case "STOP":
      case "STOPPED":
      case "PAUSED":
        return { bgColor: "#ed6c02", icon: <WarningAmberIcon sx={{ fontSize: 18 }} /> };

      case "STARTED":
      case "RUNNING":
      case "RERUNNING":
        return { bgColor: "#0288d1", icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} /> };

      case "SKIPPED":
        return { bgColor: "#757575", icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} /> };

      case "PASS":
      case "PASSED":
        return { bgColor: "#2e7d32", icon: <InfoOutlinedIcon sx={{ fontSize: 18 }} /> };

      default:
        return { bgColor: "#d32f2f", icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} /> };
    }
  };

  const issueStyle = getIssueStyle();

  const handleClickAway = () => {
    setIssueOpen(false);
  };

  const getStatusColor = () => {
    switch (status?.toUpperCase()) {
      case "PASS":
      case "PASSED":
        return "#2e7d32";
      case "INCOMPLETE":
      case "STOPP":
      case "STOP":
      case "STOPPED":
      case "PAUSED":
        return "#ed6c02";
      case "STARTED":
      case "RUNNING":
      case "RERUNNING":
        return "#0288d1";
      case "SKIPPED":
        return "#757575";
      default:
        return "#d32f2f";
    }
  };

  const getDisplayStatus = () => {
    const statusUpper = status?.toUpperCase();
    if (statusUpper === "STOPP") return "STOPPED";
    return status;
  };

  const issueIndicatorIcon = hasIssue ? (
    <Box
      sx={{
        bgcolor: issueStyle.bgColor,
        borderRadius: "50%",
        width: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        animation: issueOpen ? "none" : "pulse 2s infinite",
        "@keyframes pulse": {
          "0%": {
            boxShadow: `0 0 0 0 ${issueStyle.bgColor}99`,
          },
          "70%": {
            boxShadow: `0 0 0 8px ${issueStyle.bgColor}00`,
          },
          "100%": {
            boxShadow: `0 0 0 0 ${issueStyle.bgColor}00`,
          },
        },
      }}
      onClick={(e) => {
        e.stopPropagation();
        setIssueOpen((prev) => !prev);
      }}
    >
      <Box sx={{ color: "#fff", display: "flex", fontSize: 14 }}>{issueStyle.icon}</Box>
    </Box>
  ) : null;

  const statusText = showStatusText ? (
    <Typography sx={{ color: getStatusColor(), fontWeight: 600 }}>{getDisplayStatus()}</Typography>
  ) : null;

  if (!hasIssue && !children) {
    return statusText;
  }
  if (!hasIssue && children) {
    return (
      <Box display="inline-flex" alignItems="center">
        {children}
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box display="inline-flex" alignItems="center" gap={0.5}>
        <Tooltip
          open={issueOpen && hasIssue}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          arrow
          placement="bottom-start"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: issueStyle.bgColor,
                color: "#fff",
                maxWidth: 500,
                fontSize: 13,
                p: 1.5,
                borderRadius: 1,
                boxShadow: 3,
              },
            },
            arrow: {
              sx: { color: issueStyle.bgColor },
            },
          }}
          title={
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                {issueStyle.icon}
                <Typography fontSize={12} fontWeight={600}>
                  {issueType || getDisplayStatus() || "Issue"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIssueOpen(false);
                  }}
                  sx={{ ml: "auto", color: "#fff", p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Typography fontSize={12} sx={{ whiteSpace: "pre-wrap" }}>
                {issues}
              </Typography>
              {divergentDetails?.testStepResultId && onViewDivergence && (
                <Typography
                  component="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIssueOpen(false);
                    onViewDivergence();
                  }}
                  fontSize={12}
                  fontWeight={600}
                  sx={{
                    color: "#fff",
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    p: 0,
                    display: "block",
                    mt: 1,
                    cursor: "pointer",
                  }}
                >
                  View Divergence Details →
                </Typography>
              )}
            </Box>
          }
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            {children}
            {statusText}
            {issueIndicatorIcon}
          </Box>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

export default IssueIndicator;
